import {
  axiosConfig,
  del,
  get,
  patch,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type {
  DonationSchemePayload,
  DonationSchemeRecord,
} from "@project/types/donation-scheme";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/donation-schemes`;
const SILENT: RequestConfig = { showNotification: false };

function toFormData(payload: Partial<DonationSchemePayload>): FormData {
  const fd = new FormData();
  if (payload.title !== undefined) {
    fd.append("title", payload.title);
  }
  if (payload.sub_title !== undefined && payload.sub_title !== null) {
    fd.append("sub_title", payload.sub_title);
  }
  if (payload.type_of_donation !== undefined && payload.type_of_donation !== null) {
    fd.append("type_of_donation", String(payload.type_of_donation));
  }
  if (payload.category !== undefined && payload.category !== null) {
    fd.append("category", String(payload.category));
  }
  if (payload.sub_category !== undefined && payload.sub_category !== null) {
    fd.append("sub_category", String(payload.sub_category));
  }
  if (payload.description !== undefined && payload.description !== null) {
    fd.append("description", payload.description);
  }
  if (payload.amount_needed !== undefined && payload.amount_needed !== null) {
    fd.append("amount_needed", String(payload.amount_needed));
  }
  if (payload.is_active !== undefined && payload.is_active !== null) {
    fd.append("is_active", String(payload.is_active));
  }
  if (payload.start_date !== undefined && payload.start_date !== null) {
    fd.append("start_date", payload.start_date);
  }
  if (payload.end_date !== undefined && payload.end_date !== null) {
    fd.append("end_date", payload.end_date);
  }
  if (payload.amount_type !== undefined && payload.amount_type !== null) {
    fd.append("amount_type", payload.amount_type);
  }
  if (
    payload.allow_donation_after_target !== undefined &&
    payload.allow_donation_after_target !== null
  ) {
    fd.append(
      "allow_donation_after_target",
      String(payload.allow_donation_after_target)
    );
  }
  if (
    payload.fixed_amount !== undefined &&
    payload.fixed_amount !== null &&
    payload.fixed_amount !== ""
  ) {
    fd.append("fixed_amount", String(payload.fixed_amount));
  }
  if (
    payload.min_contribution !== undefined &&
    payload.min_contribution !== null &&
    payload.min_contribution !== ""
  ) {
    fd.append("min_contribution", String(payload.min_contribution));
  }
  if (
    payload.max_contribution !== undefined &&
    payload.max_contribution !== null &&
    payload.max_contribution !== ""
  ) {
    fd.append("max_contribution", String(payload.max_contribution));
  }
  if (payload.banner_image instanceof File) {
    fd.append("banner_image", payload.banner_image);
  }
  return fd;
}

class DonationSchemeService {
  listDonationSchemesPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<DonationSchemeRecord>> => {
    return fetchListPage<DonationSchemeRecord>(`${BASE}/`, query);
  };

  getDonationScheme = async (
    id: number
  ): Promise<DonationSchemeRecord | undefined> => {
    const data = await get<
      ApiItemResponse<DonationSchemeRecord> | DonationSchemeRecord
    >(`${BASE}/${id}/`, SILENT_LIST);
    return unwrapItem(data);
  };

  create = async (
    payload: DonationSchemePayload
  ): Promise<DonationSchemeRecord> => {
    const fd = toFormData(payload);
    const response = await axiosConfig.post<
      ApiItemResponse<DonationSchemeRecord> | DonationSchemeRecord
    >(`${BASE}/`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to create donation scheme.");
    }
    return item;
  };

  update = async (
    id: number,
    payload: Partial<DonationSchemePayload>
  ): Promise<DonationSchemeRecord | undefined> => {
    const fd = toFormData(payload);
    const response = await axiosConfig.patch<
      ApiItemResponse<DonationSchemeRecord> | DonationSchemeRecord
    >(`${BASE}/${id}/`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return unwrapItem(response.data);
  };

  delete = async (id: number): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };
}

export const donationSchemeService = new DonationSchemeService();
